import { Sort } from '@angular/material/sort';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { finalize, map, startWith, take } from 'rxjs/operators';
import { IDownloadDatasource } from '../../model';

export abstract class DownloadsDatasource<T> implements IDownloadDatasource<T> {
  private hasErrorSubj = new BehaviorSubject<boolean>(false);
  private isLoadingSubj = new BehaviorSubject<boolean>(false);
  private downloadsSubj = new BehaviorSubject<Array<T>>([]);
  private sortSubj = new Subject<Sort>();
  private downloads$ = this.downloadsSubj.asObservable();
  private sort$ = this.sortSubj.asObservable();

  public hasError$ = this.hasErrorSubj.asObservable();
  public isLoading$ = this.isLoadingSubj.asObservable();
  public sortedDownloads$ = combineLatest([
    this.downloads$,
    this.sort$.pipe(startWith<Sort>({ active: '', direction: '' })),
  ]).pipe(
    map(([downloads, sortEvent]) => this.sortData(sortEvent, [...downloads])),
  );

  public connect(): Observable<readonly T[]> {
    return this.sortedDownloads$;
  }

  public disconnect(): void {}

  public cleanUp(): void {
    this.hasErrorSubj.complete();
    this.isLoadingSubj.complete();
    this.downloadsSubj.complete();
    this.sortSubj.complete();
  }

  public updateSorting(sort: Sort): void {
    this.sortSubj.next(sort);
  }

  public addDownloads(loadRequest: Observable<Array<T>>): void {
    this.handleLoadRequest(loadRequest).subscribe({
      next: products => {
        this.hasErrorSubj.next(false);
        this.downloadsSubj.next([
          ...this.downloadsSubj.getValue(),
          ...products,
        ]);
      },
      error: error => {
        this.hasErrorSubj.next(true);
        console.log(error);
      },
    });
  }

  public replaceDownloads(loadRequest: Observable<Array<T>>): void {
    this.handleLoadRequest(loadRequest).subscribe({
      next: products => {
        this.hasErrorSubj.next(false);
        this.downloadsSubj.next(products);
      },
      error: error => {
        this.hasErrorSubj.next(true);
        console.log(error);

        this.downloadsSubj.next([]);
      },
    });
  }

  private handleLoadRequest(
    loadRequest: Observable<Array<T>>,
  ): Observable<Array<T>> {
    this.hasErrorSubj.next(false);
    this.isLoadingSubj.next(true);
    return loadRequest.pipe(
      take(1),
      finalize(() => this.isLoadingSubj.next(false)),
    );
  }

  abstract sortData(event: Sort, data: Array<T>): Array<T>;

  compareDateStrings(a: string | null, b: string | null): number {
    if (a !== null && b === null) {
      return 1;
    }

    if (a === null && b !== null) {
      return -1;
    }

    if (a !== null && b !== null) {
      return new Date(a).valueOf() - new Date(b).valueOf();
    }

    return 0;
  }

  compareStrings(a: string, b: string): number {
    return a === b ? 0 : a > b ? 1 : -1;
  }
}
